import { useState } from 'react';
import {
  ArrowDownIcon,
  ArrowUpDownIcon,
  ArrowUpIcon,
  MinusIcon,
  PlusIcon,
  Trash2Icon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import {
  ContainerTypeDropdownContent,
  GroupedItemTypeDropdownContent,
  type ItemTypeGroup,
  type MinimalContainerType,
} from '../table/ContainerTypeDropdown';
import type {
  EventItemFormState,
  EventItemTypeLineFormState,
  EventUntrackedItemFormState,
} from './useEventOrderForm';
import type { EventItemTypeOption } from '../../utils/useEventItemTypeOptions';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group';
import { Kbd } from '@/components/ui/kbd';
import { ContainerTypeSegment } from '@/graphql/generatedTypes';
import type { OpsOrderDetailFragment } from '@/graphql/fragments/opsOrderDetail.generated';
import { ItemTypeDisplay } from '@/components/ItemType/ItemTypeDisplay';
import i18n from '@/utils/i18n';

type UnifiedRow =
  | { kind: 'container'; item: EventItemFormState }
  | { kind: 'untracked'; item: EventUntrackedItemFormState }
  | { kind: 'item_type'; item: EventItemTypeLineFormState };

export type BufferOrderedContainerType = MinimalContainerType & {
  segment?: ContainerTypeSegment | null;
  packingUnit?: number;
};

/** A cleared or non-numeric input means zero, never NaN. */
function parseCount(raw: string): number {
  const parsed = parseInt(raw, 10);
  return Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
}

function selectOnFocus(e: React.FocusEvent<HTMLInputElement>) {
  const target = e.target;
  requestAnimationFrame(() => {
    target.select();
  });
}

interface EventItemLocationCardProps {
  locationName: string;
  items: EventItemFormState[];
  untrackedItems?: EventUntrackedItemFormState[];
  itemTypeLines?: EventItemTypeLineFormState[];
  onBoxCountChange: (itemId: string, value: number) => void;
  onIncrement: (itemId: string, delta: number) => void;
  onUntrackedAmountChange?: (itemId: string, value: number) => void;
  onItemTypeLineAmountChange?: (itemId: string, value: number) => void;
  onRemoveItem?: (itemId: string) => void;
  onRemoveUntrackedItem?: (itemId: string) => void;
  onRemoveItemTypeLine?: (itemId: string) => void;
  allowedContainerTypes?: BufferOrderedContainerType[];
  allowedUntrackedTypes?: MinimalContainerType[];
  allowedStationTypes?: EventItemTypeOption[];
  allowedAssetTypes?: EventItemTypeOption[];
  isBufferOrder?: boolean;
  eventOrderedTotals?: OpsOrderDetailFragment['event_ordered_totals'];
  disabled?: boolean;
  onAddItem?: (
    typeId: number,
    typeName: string,
    imageUrl: string | null,
    boxes: number,
  ) => void;
  onAddUntrackedItem?: (
    typeId: number,
    typeName: string,
    imageUrl: string | null,
  ) => void;
  onAddItemTypeLine?: (
    itemTypeId: number,
    typeName: string,
    imageUrl: string | null,
    hexCode: string | null,
    amount: number,
  ) => void;
}

export function EventItemLocationCard({
  locationName,
  items,
  untrackedItems,
  itemTypeLines,
  onBoxCountChange,
  onIncrement,
  onUntrackedAmountChange,
  onItemTypeLineAmountChange,
  onRemoveItem,
  onRemoveUntrackedItem,
  onRemoveItemTypeLine,
  allowedContainerTypes,
  allowedUntrackedTypes,
  allowedStationTypes,
  allowedAssetTypes,
  isBufferOrder,
  eventOrderedTotals,
  disabled,
  onAddItem,
  onAddUntrackedItem,
  onAddItemTypeLine,
}: EventItemLocationCardProps) {
  const [addPopoverOpen, setAddPopoverOpen] = useState(false);
  // The row order is frozen when a header is clicked. Sorting live on every
  // edit made a row jump away the moment its buffer changed, so the +/− click
  // seemed to do nothing while the value moved to another position.
  const [bufferSort, setBufferSort] = useState<{
    key: SortKey;
    dir: 'asc' | 'desc';
    order: string[];
  } | null>(null);
  const hexMap = new Map(
    allowedContainerTypes?.map((ct) => [ct.id, ct.hexCode ?? null]) ?? [],
  );
  const existingContainerTypeIds = new Set(items.map((item) => item.type_id));
  const allowedContainerTypesForItemLocation = allowedContainerTypes?.filter(
    (ct) => !existingContainerTypeIds.has(ct.id),
  );

  const untrackedTypeIds = new Set(
    untrackedItems?.map((item) => item.type_id) ?? [],
  );

  const allowedUntrackedFiltered = allowedUntrackedTypes?.filter(
    (ct) => !untrackedTypeIds.has(ct.id),
  );

  const existingItemTypeIds = new Set(
    itemTypeLines?.map((line) => line.item_type_id) ?? [],
  );
  const toDropdownItems = (
    options: EventItemTypeOption[] | undefined,
  ): MinimalContainerType[] =>
    (options ?? [])
      .filter((opt) => !existingItemTypeIds.has(opt.item_type_id))
      .map((opt) => ({
        id: opt.item_type_id,
        name: opt.name,
        imageUrl: opt.imageUrl,
        hexCode: opt.hexCode,
      }));
  const stationDropdownItems = toDropdownItems(allowedStationTypes);
  const assetDropdownItems = toDropdownItems(allowedAssetTypes);

  const useGroupedDropdown =
    (allowedUntrackedTypes && allowedUntrackedTypes.length > 0) ||
    (allowedStationTypes && allowedStationTypes.length > 0) ||
    (allowedAssetTypes && allowedAssetTypes.length > 0);

  const dropdownGroups: ItemTypeGroup[] | null = (() => {
    if (!useGroupedDropdown) return null;
    const groups: ItemTypeGroup[] = [];
    const filteredContainers = allowedContainerTypesForItemLocation ?? [];
    if (filteredContainers.length > 0) {
      groups.push({
        kind: 'container',
        heading: i18n.t('Order:dropdownContainers'),
        items: filteredContainers,
      });
    }
    if (stationDropdownItems.length > 0) {
      groups.push({
        kind: 'container_station',
        heading: i18n.t('Order:dropdownContainerStations'),
        items: stationDropdownItems,
      });
    }
    if (assetDropdownItems.length > 0) {
      groups.push({
        kind: 'asset',
        heading: i18n.t('Order:dropdownAssets'),
        items: assetDropdownItems,
      });
    }
    if (allowedUntrackedFiltered && allowedUntrackedFiltered.length > 0) {
      groups.push({
        kind: 'untracked',
        heading: i18n.t('Order:dropdownOtherItems'),
        items: allowedUntrackedFiltered,
      });
    }
    return groups;
  })();

  const handleGroupedSelect = (
    ct: MinimalContainerType,
    group: ItemTypeGroup,
  ) => {
    switch (group.kind) {
      case 'container':
        onAddItem?.(ct.id, ct.name, ct.imageUrl ?? null, 0);
        break;
      case 'container_station':
      case 'asset':
        onAddItemTypeLine?.(
          ct.id,
          ct.name,
          ct.imageUrl ?? null,
          ct.hexCode ?? null,
          0,
        );
        break;
      case 'untracked':
        onAddUntrackedItem?.(ct.id, ct.name, ct.imageUrl ?? null);
        break;
    }
    setAddPopoverOpen(false);
  };

  const hasAddableItems = dropdownGroups
    ? dropdownGroups.length > 0
    : (allowedContainerTypesForItemLocation?.length ?? 0) > 0;

  const allRows: UnifiedRow[] = [
    ...items.map((item): UnifiedRow => ({ kind: 'container', item })),
    ...(itemTypeLines ?? []).map(
      (item): UnifiedRow => ({ kind: 'item_type', item }),
    ),
    ...(untrackedItems ?? []).map(
      (item): UnifiedRow => ({ kind: 'untracked', item }),
    ),
  ];

  const canRemove =
    !disabled &&
    (onRemoveItem != null ||
      onRemoveUntrackedItem != null ||
      onRemoveItemTypeLine != null);

  const bufferRows = getBufferRows({
    isBufferOrder,
    allowedContainerTypes,
    allowedUntrackedTypes,
    allowedStationTypes,
    allowedAssetTypes,
    eventOrderedTotals,
    items,
    untrackedItems,
    itemTypeLines,
  });

  const sortedBufferRows = (() => {
    if (!bufferSort) return bufferRows;
    const position = new Map(bufferSort.order.map((k, i) => [k, i]));
    return [...bufferRows].sort(
      (a, b) =>
        (position.get(bufferRowKey(a)) ?? Infinity) -
        (position.get(bufferRowKey(b)) ?? Infinity),
    );
  })();

  const toggleSort = (key: SortKey) => {
    setBufferSort((prev) => {
      if (prev?.key === key && prev.dir === 'desc') return null;
      const dir = prev?.key === key ? 'desc' : 'asc';
      const sorted = [...bufferRows].sort((a, b) => {
        if (key === 'typeName') return a.typeName.localeCompare(b.typeName);
        return (a[key] ?? -1) - (b[key] ?? -1);
      });
      if (dir === 'desc') sorted.reverse();
      return { key, dir, order: sorted.map(bufferRowKey) };
    });
  };

  return (
    <Card>
      <CardHeader className="tw:flex tw:flex-row tw:items-center tw:justify-between">
        <CardTitle>{locationName}</CardTitle>
        {(onAddItem || onAddUntrackedItem || onAddItemTypeLine) &&
          !hasAddableItems && (
            // Everything the event allows is already on this order. Keep the
            // button in place, greyed out, so the empty picker does not read
            // as a missing feature.
            <span title={i18n.t('Order:nothingLeftToAdd')}>
              <Button variant="secondary" size="sm" disabled>
                {i18n.t('Order:addItemToOrder')}
              </Button>
            </span>
          )}
        {(onAddItem || onAddUntrackedItem || onAddItemTypeLine) &&
          hasAddableItems && (
            <Popover open={addPopoverOpen} onOpenChange={setAddPopoverOpen}>
              <PopoverTrigger asChild>
                <Button variant="secondary" size="sm">
                  {i18n.t('Order:addItemToOrder')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="tw:w-[380px] tw:p-0" align="end">
                {dropdownGroups ? (
                  <GroupedItemTypeDropdownContent
                    groups={dropdownGroups}
                    onSelect={handleGroupedSelect}
                    {...(isBufferOrder && {
                      searchPlaceholder: i18n.t('Order:filterSearchOrderItem'),
                      emptyText: i18n.t('Order:filterNoOrderItems'),
                    })}
                  />
                ) : (
                  <ContainerTypeDropdownContent
                    containerTypes={allowedContainerTypesForItemLocation ?? []}
                    onSelect={(ct) => {
                      onAddItem?.(ct.id, ct.name, ct.imageUrl ?? null, 0);
                      setAddPopoverOpen(false);
                    }}
                    {...(isBufferOrder && {
                      searchPlaceholder: i18n.t('Order:filterSearchOrderItem'),
                      emptyText: i18n.t('Order:filterNoOrderItems'),
                    })}
                  />
                )}
              </PopoverContent>
            </Popover>
          )}
      </CardHeader>
      <CardContent>
        {isBufferOrder ? (
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHead
                  sortKey="typeName"
                  currentSort={bufferSort}
                  onToggle={toggleSort}
                  className="tw:max-w-[300px]"
                >
                  {i18n.t('Order:tableItemType')}
                </SortableHead>
                <SortableHead
                  sortKey="totalOrdered"
                  currentSort={bufferSort}
                  onToggle={toggleSort}
                  className="tw:w-[170px]"
                >
                  {i18n.t('Order:tableTotalOrdered')}
                </SortableHead>
                <SortableHead
                  sortKey="buffer"
                  currentSort={bufferSort}
                  onToggle={toggleSort}
                  className="tw:w-[240px]"
                >
                  {i18n.t('Order:tableBuffer')}
                </SortableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedBufferRows.map((row) => (
                <BufferItemRow
                  key={bufferRowKey(row)}
                  row={row}
                  disabled={disabled}
                  onBoxCountChange={onBoxCountChange}
                  onIncrement={onIncrement}
                  onUntrackedAmountChange={onUntrackedAmountChange}
                  onAddItem={onAddItem}
                  onAddUntrackedItem={onAddUntrackedItem}
                  onItemTypeLineAmountChange={onItemTypeLineAmountChange}
                  onAddItemTypeLine={onAddItemTypeLine}
                />
              ))}
            </TableBody>
          </Table>
        ) : allRows.length === 0 ? (
          <p className="tw:text-muted-foreground tw:text-sm tw:py-4 tw:text-center">
            {i18n.t('Common:noItems')}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="tw:max-w-[300px]">
                  {i18n.t('Order:tableItemType')}
                </TableHead>
                <TableHead>{i18n.t('Order:tableQuantity')}</TableHead>
                {canRemove && <TableHead className="tw:w-12" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {allRows.map((row) => (
                <EventItemRow
                  key={`${row.kind}-${row.item.id}`}
                  row={row}
                  hexMap={hexMap}
                  disabled={disabled}
                  showRemoveColumn={canRemove}
                  onBoxCountChange={onBoxCountChange}
                  onIncrement={onIncrement}
                  onUntrackedAmountChange={onUntrackedAmountChange}
                  onItemTypeLineAmountChange={onItemTypeLineAmountChange}
                  onRemoveItem={onRemoveItem}
                  onRemoveUntrackedItem={onRemoveUntrackedItem}
                  onRemoveItemTypeLine={onRemoveItemTypeLine}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function getSegmentLabel(
  segment: ContainerTypeSegment | null | undefined,
): string | null {
  if (!segment) return null;
  return segment === ContainerTypeSegment.Cutlery ? 'xS' : 'xL';
}

function unitLabel(kind: UnifiedRow['kind'] | BufferRow['kind']): string {
  return kind === 'container'
    ? i18n.t('EventOrder:boxes')
    : i18n.t('EventOrder:pcs');
}

interface QuantityStepperProps {
  value: number;
  unit: string;
  segmentLabel?: string | null;
  disabled?: boolean;
  onChange: (value: number) => void;
  onIncrement: (delta: number) => void;
}

/**
 * The one quantity control every line uses: containers count boxes, item
 * type and untracked lines count pieces — the unit tag says which.
 */
function QuantityStepper({
  value,
  unit,
  segmentLabel,
  disabled,
  onChange,
  onIncrement,
}: QuantityStepperProps) {
  return (
    <ButtonGroup>
      <Button
        variant="outline"
        size="icon"
        disabled={disabled || value <= 0}
        onClick={() => onIncrement(-1)}
      >
        <MinusIcon className="tw:size-4" />
      </Button>
      {/* The unit pills keep their width, so the number needs a floor of its
          own or a narrow column squeezes it to zero — the table then widens
          this cell and wraps the item name instead. */}
      <InputGroup className="tw:w-40 tw:min-w-36 tw:shrink-0">
        <InputGroupInput
          type="number"
          min={0}
          value={value}
          disabled={disabled}
          onFocus={selectOnFocus}
          onChange={(e) => onChange(parseCount(e.target.value))}
          className="tw:min-w-10 tw:px-1 tw:text-center tw:no-spinners"
        />
        <InputGroupAddon align="inline-end" className="tw:gap-1">
          {segmentLabel && <Kbd className="tw:px-1">{segmentLabel}</Kbd>}
          <Kbd className="tw:px-1">{unit}</Kbd>
        </InputGroupAddon>
      </InputGroup>
      <Button
        variant="outline"
        size="icon"
        disabled={disabled}
        onClick={() => onIncrement(1)}
      >
        <PlusIcon className="tw:size-4" />
      </Button>
    </ButtonGroup>
  );
}

interface EventItemRowProps {
  row: UnifiedRow;
  hexMap: Map<number, string | null>;
  disabled?: boolean;
  showRemoveColumn: boolean;
  onBoxCountChange: (itemId: string, value: number) => void;
  onIncrement: (itemId: string, delta: number) => void;
  onUntrackedAmountChange?: (itemId: string, value: number) => void;
  onItemTypeLineAmountChange?: (itemId: string, value: number) => void;
  onRemoveItem?: (itemId: string) => void;
  onRemoveUntrackedItem?: (itemId: string) => void;
  onRemoveItemTypeLine?: (itemId: string) => void;
}

function EventItemRow({
  row,
  hexMap,
  disabled,
  showRemoveColumn,
  onBoxCountChange,
  onIncrement,
  onUntrackedAmountChange,
  onItemTypeLineAmountChange,
  onRemoveItem,
  onRemoveUntrackedItem,
  onRemoveItemTypeLine,
}: EventItemRowProps) {
  const { item } = row;
  const onAmountChange =
    row.kind === 'item_type'
      ? onItemTypeLineAmountChange
      : onUntrackedAmountChange;
  const onRemove =
    row.kind === 'container'
      ? onRemoveItem
      : row.kind === 'item_type'
        ? onRemoveItemTypeLine
        : onRemoveUntrackedItem;

  const quantity = row.kind === 'container' ? row.item.boxes : item.amount;
  const hexCode =
    row.kind === 'container'
      ? (row.item.hexCode ?? hexMap.get(row.item.type_id) ?? null)
      : row.kind === 'item_type'
        ? row.item.hexCode
        : null;

  return (
    <TableRow>
      <TableCell>
        <div className="tw:flex tw:items-center tw:gap-2">
          <ItemTypeDisplay
            alt={item.typeName}
            hexCode={hexCode}
            imageUrl={item.imageUrl}
            className="tw:size-10"
            showLargeImageOnHover
          />
          <span className="tw:text-sm tw:font-medium">{item.typeName}</span>
        </div>
      </TableCell>
      <TableCell>
        <div className="tw:flex tw:flex-col tw:gap-1">
          <QuantityStepper
            value={quantity}
            unit={unitLabel(row.kind)}
            segmentLabel={
              row.kind === 'container'
                ? getSegmentLabel(row.item.segment)
                : null
            }
            disabled={disabled}
            onChange={(value) =>
              row.kind === 'container'
                ? onBoxCountChange(item.id, value)
                : onAmountChange?.(item.id, value)
            }
            onIncrement={(delta) =>
              row.kind === 'container'
                ? onIncrement(item.id, delta)
                : onAmountChange?.(item.id, Math.max(0, item.amount + delta))
            }
          />
          {row.kind === 'container' && (
            <span className="tw:text-muted-foreground tw:text-xs">
              {i18n.t('Order:quantityPiecesTotal', { count: row.item.amount })}
            </span>
          )}
        </div>
      </TableCell>
      {showRemoveColumn && (
        <TableCell className="tw:text-right">
          {onRemove && (
            <Button
              variant="ghost"
              size="icon"
              aria-label={i18n.t('Order:removeLine')}
              title={i18n.t('Order:removeLine')}
              onClick={() => onRemove(item.id)}
            >
              <Trash2Icon className="tw:size-4" />
            </Button>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}

interface BufferRow {
  kind: 'container' | 'untracked' | 'item_type';
  typeId: number;
  typeName: string;
  imageUrl: string | null;
  hexCode: string | null;
  segment: ContainerTypeSegment | null;
  /** Null when the event-wide totals do not track this kind of line. */
  totalOrdered: number | null;
  buffer: number;
  formItemId: string | null;
  packingUnit: number;
  readOnly?: boolean;
}

function getBufferRows({
  isBufferOrder,
  allowedContainerTypes,
  allowedUntrackedTypes,
  allowedStationTypes,
  allowedAssetTypes,
  eventOrderedTotals,
  items,
  untrackedItems,
  itemTypeLines,
}: Pick<
  EventItemLocationCardProps,
  | 'isBufferOrder'
  | 'allowedContainerTypes'
  | 'allowedUntrackedTypes'
  | 'allowedStationTypes'
  | 'allowedAssetTypes'
  | 'eventOrderedTotals'
  | 'items'
  | 'untrackedItems'
  | 'itemTypeLines'
>): BufferRow[] {
  if (!isBufferOrder) return [];

  const rows: BufferRow[] = [];
  const allowedUntrackedIdSet = new Set(
    allowedUntrackedTypes?.map((ct) => ct.id) ?? [],
  );
  const containerTotalsMap = new Map(
    eventOrderedTotals?.container_types.map((t) => [
      t.type.id,
      {
        ordered: t.ordered,
        name: t.type.name ?? '',
        imageUrl: t.type.image_url ?? null,
      },
    ]) ?? [],
  );
  const itemTotalsMap = new Map(
    eventOrderedTotals?.item_types.map((t) => [t.type.id, t.ordered]) ?? [],
  );
  const containerFormMap = new Map(items.map((item) => [item.type_id, item]));
  const seenContainerTypeIds = new Set<number>();

  for (const ct of allowedContainerTypes ?? []) {
    seenContainerTypeIds.add(ct.id);
    const formItem = containerFormMap.get(ct.id);
    rows.push({
      kind: 'container',
      typeId: ct.id,
      typeName: ct.name,
      imageUrl: ct.imageUrl ?? null,
      hexCode: ct.hexCode ?? null,
      segment: ct.segment ?? null,
      totalOrdered: containerTotalsMap.get(ct.id)?.ordered ?? 0,
      buffer: formItem?.boxes ?? 0,
      formItemId: formItem?.id ?? null,
      packingUnit: ct.packingUnit ?? 1,
    });
  }

  for (const formItem of items) {
    if (seenContainerTypeIds.has(formItem.type_id)) continue;
    seenContainerTypeIds.add(formItem.type_id);
    rows.push({
      kind: 'container',
      typeId: formItem.type_id,
      typeName: formItem.typeName,
      imageUrl: formItem.imageUrl,
      hexCode: formItem.hexCode ?? null,
      segment: formItem.segment ?? null,
      totalOrdered: containerTotalsMap.get(formItem.type_id)?.ordered ?? 0,
      buffer: formItem.boxes,
      formItemId: formItem.id,
      packingUnit: formItem.packingUnit,
    });
  }

  for (const [typeId, totals] of containerTotalsMap) {
    if (seenContainerTypeIds.has(typeId)) continue;
    seenContainerTypeIds.add(typeId);
    const formItem = containerFormMap.get(typeId);
    rows.push({
      kind: 'container',
      typeId,
      typeName: totals.name,
      imageUrl: totals.imageUrl,
      hexCode: null,
      segment: null,
      totalOrdered: totals.ordered,
      buffer: formItem?.boxes ?? 0,
      formItemId: formItem?.id ?? null,
      packingUnit: 1,
      readOnly: true,
    });
  }

  // ItemType lines (stations / assets): one row per type the event allows,
  // plus any form line whose type is no longer offered (kept editable — it is
  // on the order). Totals come from event_ordered_totals.item_types.
  const itemTypeFormMap = new Map(
    (itemTypeLines ?? []).map((line) => [line.item_type_id, line]),
  );
  const seenItemTypeIds = new Set<number>();
  for (const opt of [
    ...(allowedStationTypes ?? []),
    ...(allowedAssetTypes ?? []),
  ]) {
    if (seenItemTypeIds.has(opt.item_type_id)) continue;
    seenItemTypeIds.add(opt.item_type_id);
    const formLine = itemTypeFormMap.get(opt.item_type_id);
    rows.push({
      kind: 'item_type',
      typeId: opt.item_type_id,
      typeName: opt.name,
      imageUrl: opt.imageUrl,
      hexCode: opt.hexCode,
      segment: null,
      totalOrdered: itemTotalsMap.get(opt.item_type_id) ?? 0,
      buffer: formLine?.amount ?? 0,
      formItemId: formLine?.id ?? null,
      packingUnit: 1,
    });
  }
  for (const line of itemTypeLines ?? []) {
    if (seenItemTypeIds.has(line.item_type_id)) continue;
    seenItemTypeIds.add(line.item_type_id);
    rows.push({
      kind: 'item_type',
      typeId: line.item_type_id,
      typeName: line.typeName,
      imageUrl: line.imageUrl,
      hexCode: line.hexCode,
      segment: null,
      totalOrdered: itemTotalsMap.get(line.item_type_id) ?? 0,
      buffer: line.amount,
      formItemId: line.id,
      packingUnit: 1,
    });
  }

  // Untracked lines (flyers, return boxes) are not part of the event-wide
  // ordered totals, so there is nothing to show as "total ordered".
  for (const item of untrackedItems ?? []) {
    rows.push({
      kind: 'untracked',
      typeId: item.type_id,
      typeName: item.typeName,
      imageUrl: item.imageUrl,
      hexCode: null,
      segment: null,
      totalOrdered: null,
      buffer: item.amount,
      formItemId: item.id,
      packingUnit: 1,
      readOnly: !allowedUntrackedIdSet.has(item.type_id),
    });
  }

  return rows;
}

const bufferRowKey = (row: BufferRow) => `${row.kind}-${row.typeId}`;

interface BufferItemRowProps {
  row: BufferRow;
  disabled?: boolean;
  onBoxCountChange: (itemId: string, value: number) => void;
  onIncrement: (itemId: string, delta: number) => void;
  onUntrackedAmountChange?: (itemId: string, value: number) => void;
  onAddItem?: (
    typeId: number,
    typeName: string,
    imageUrl: string | null,
    boxes: number,
  ) => void;
  onAddUntrackedItem?: (
    typeId: number,
    typeName: string,
    imageUrl: string | null,
  ) => void;
  onItemTypeLineAmountChange?: (itemId: string, value: number) => void;
  onAddItemTypeLine?: (
    itemTypeId: number,
    typeName: string,
    imageUrl: string | null,
    hexCode: string | null,
    amount: number,
  ) => void;
}

function BufferItemRow({
  row,
  disabled,
  onBoxCountChange,
  onIncrement,
  onUntrackedAmountChange,
  onAddItem,
  onAddUntrackedItem,
  onItemTypeLineAmountChange,
  onAddItemTypeLine,
}: BufferItemRowProps) {
  const segmentLabel = getSegmentLabel(row.segment);
  const unit = unitLabel(row.kind);

  const handleIncrement = (delta: number) => {
    if (row.formItemId) {
      if (row.kind === 'container') {
        onIncrement(row.formItemId, delta);
      } else if (row.kind === 'item_type') {
        onItemTypeLineAmountChange?.(
          row.formItemId,
          Math.max(0, row.buffer + delta),
        );
      } else {
        onUntrackedAmountChange?.(
          row.formItemId,
          Math.max(0, row.buffer + delta),
        );
      }
    } else if (delta > 0) {
      if (row.kind === 'container') {
        onAddItem?.(row.typeId, row.typeName, row.imageUrl, delta);
      } else if (row.kind === 'item_type') {
        onAddItemTypeLine?.(
          row.typeId,
          row.typeName,
          row.imageUrl,
          row.hexCode,
          delta,
        );
      } else {
        onAddUntrackedItem?.(row.typeId, row.typeName, row.imageUrl);
      }
    }
  };

  const handleChange = (value: number) => {
    if (row.formItemId) {
      if (row.kind === 'container') {
        onBoxCountChange(row.formItemId, value);
      } else if (row.kind === 'item_type') {
        onItemTypeLineAmountChange?.(row.formItemId, value);
      } else {
        onUntrackedAmountChange?.(row.formItemId, value);
      }
    } else if (value > 0) {
      if (row.kind === 'container') {
        onAddItem?.(row.typeId, row.typeName, row.imageUrl, value);
      } else if (row.kind === 'item_type') {
        onAddItemTypeLine?.(
          row.typeId,
          row.typeName,
          row.imageUrl,
          row.hexCode,
          value,
        );
      } else {
        onAddUntrackedItem?.(row.typeId, row.typeName, row.imageUrl);
      }
    }
  };

  return (
    <TableRow>
      <TableCell className="tw:max-w-[300px]">
        <div className="tw:flex tw:items-center tw:gap-2 tw:min-w-0">
          <ItemTypeDisplay
            alt={row.typeName}
            hexCode={row.hexCode}
            imageUrl={row.imageUrl}
            className="tw:size-10 tw:shrink-0"
            showLargeImageOnHover
          />
          <span className="tw:text-sm tw:font-medium tw:truncate">
            {row.typeName}
          </span>
        </div>
      </TableCell>
      <TableCell>
        {row.totalOrdered == null ? (
          <span className="tw:text-muted-foreground tw:text-sm">–</span>
        ) : (
          <InputGroup className="tw:w-40 tw:min-w-36 tw:shrink-0">
            <InputGroupInput
              type="number"
              value={row.totalOrdered}
              disabled
              className="tw:min-w-10 tw:px-1 tw:text-center tw:no-spinners"
            />
            <InputGroupAddon align="inline-end" className="tw:gap-1">
              {segmentLabel && <Kbd className="tw:px-1">{segmentLabel}</Kbd>}
              <Kbd className="tw:px-1">{unit}</Kbd>
            </InputGroupAddon>
          </InputGroup>
        )}
      </TableCell>
      <TableCell>
        <QuantityStepper
          value={row.buffer}
          unit={unit}
          segmentLabel={segmentLabel}
          disabled={disabled || row.readOnly}
          onChange={handleChange}
          onIncrement={handleIncrement}
        />
      </TableCell>
    </TableRow>
  );
}

type SortKey = 'typeName' | 'totalOrdered' | 'buffer';

function SortableHead({
  sortKey,
  currentSort,
  onToggle,
  className,
  children,
}: {
  sortKey: SortKey;
  currentSort: { key: SortKey; dir: 'asc' | 'desc' } | null;
  onToggle: (key: SortKey) => void;
  className?: string;
  children: React.ReactNode;
}) {
  const active = currentSort?.key === sortKey;
  return (
    <TableHead className={className}>
      <button
        type="button"
        className="tw:inline-flex tw:items-center tw:gap-1 tw:cursor-pointer tw:select-none"
        onClick={() => onToggle(sortKey)}
      >
        {children}
        {active && currentSort.dir === 'asc' ? (
          <ArrowUpIcon className="tw:size-3.5" />
        ) : active && currentSort.dir === 'desc' ? (
          <ArrowDownIcon className="tw:size-3.5" />
        ) : (
          <ArrowUpDownIcon className="tw:size-3.5 tw:text-muted-foreground" />
        )}
      </button>
    </TableHead>
  );
}
